import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Typography,
  Paper,
  CircularProgress,
  Box,
  Divider,
  Tooltip,
} from "@mui/material";
import MoodIcon from "@mui/icons-material/Mood";
import MoodBadIcon from "@mui/icons-material/MoodBad";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import ShieldIcon from "@mui/icons-material/Shield";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const SentimentBadge = ({ type, count, total }) => {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
  
  const config = {
    positive: { color: "text-green-500", bg: "bg-green-50 dark:bg-green-900/20", label: "Positive", icon: <MoodIcon fontSize="small" /> },
    negative: { color: "text-red-500", bg: "bg-red-50 dark:bg-red-900/20", label: "Negative", icon: <MoodBadIcon fontSize="small" /> },
    neutral: { color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-900/20", label: "Neutral", icon: <SentimentSatisfiedIcon fontSize="small" /> },
    scam_shield: { color: "text-purple-500", bg: "bg-purple-50 dark:bg-purple-900/20", label: "Troll Shield", icon: <ShieldIcon fontSize="small" /> },
  };

  const c = config[type] || config.neutral;

  return (
    <div className={`flex flex-col items-center p-4 rounded-3xl ${c.bg} flex-1 min-w-[100px] transition-all hover:scale-105 duration-300`}>
      <div className={`${c.color} mb-2`}>{c.icon}</div>
      <Typography className="text-2xl font-black text-gray-800 dark:text-gray-100">{percentage}%</Typography>
      <Typography variant="caption" className="text-gray-400 font-bold uppercase tracking-tighter">{c.label}</Typography>
    </div>
  );
};

const AIEngagementHub = ({ token, pageId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (token && pageId) {
      fetchEngagementData();
    }
  }, [token, pageId]);

  const fetchEngagementData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/report/engagement-hub?pageId=${pageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response.data);
    } catch (err) {
      console.error("Engagement Hub fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <Box className="flex justify-center p-12">
      <CircularProgress thickness={6} size={40} />
    </Box>
  );

  if (!data) return null;

  const total = Object.values(data.stats).reduce((a, b) => a + b, 0);

  return (
    <div className="flex flex-col gap-8">
      {/* Sentiment Overview */}
      <div>
        <div className="flex items-center gap-2 mb-4">
           <Typography variant="subtitle1" className="font-bold text-gray-700 dark:text-gray-300">
             Brand Sentiment Snapshot (30d)
           </Typography>
           <Tooltip title="Real-time sentiment of comments your AI has analyzed and responded to.">
              <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.5 }} />
           </Tooltip>
        </div>
        
        <div className="flex flex-wrap gap-4">
          <SentimentBadge type="positive" count={data.stats.positive} total={total} />
          <SentimentBadge type="neutral" count={data.stats.neutral} total={total} />
          <SentimentBadge type="negative" count={data.stats.negative} total={total} />
          <SentimentBadge type="scam_shield" count={data.stats.scam_shield} total={total} />
        </div>
      </div>

      <Divider className="opacity-50" />

      {/* Reputation Guard Log */}
      <div>
        <Typography variant="subtitle1" className="font-bold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <ShieldIcon className="text-purple-500" fontSize="small" />
          Recent Reputation Guard Actions
        </Typography>

        <div className="flex flex-col gap-3">
          {data.rebuttals.length === 0 ? (
            <Typography variant="body2" className="text-gray-400 italic py-4">
              All quiet. No recent brand threats or negative comments detected by AI.
            </Typography>
          ) : (
            data.rebuttals.map((r, i) => (
              <Paper key={i} className="p-4 rounded-2xl bg-gray-50/50 dark:bg-gray-800/40 border-none shadow-none">
                <div className="flex justify-between items-start mb-2">
                   <div className="flex items-center gap-2">
                      <span className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase">
                        {r.aiSentiment === 'scam_shield' ? 'Scam Shield' : 'Complaint'}
                      </span>
                      <Typography variant="caption" className="text-gray-400 font-bold">
                        {r.customerName} commented:
                      </Typography>
                   </div>
                   <Typography variant="caption" className="text-gray-400">
                      {new Date(r.createdAt).toLocaleDateString()}
                   </Typography>
                </div>
                
                <Typography className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4 pl-3 border-l-2 border-gray-200">
                   "{r.commentText}"
                </Typography>

                <div className="bg-white/80 dark:bg-gray-800/80 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                   <Typography variant="caption" className="text-primary-600 dark:text-primary-400 font-black block mb-1">AI DEFENSE POSTED:</Typography>
                   <Typography className="text-[13px] text-gray-600 dark:text-gray-400 italic">
                      "{r.aiResponseText}"
                   </Typography>
                </div>
              </Paper>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AIEngagementHub;
